/**
 * GSAP Card & Gallery Animations Module
 * Handles story pillars entrance and 3D coverflow dynamics
 */

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

let coverflowInitialized = false;
let storyPillarsInitialized = false;

/**
 * Initializes global animations: Story pillars and 3D gallery coverflow
 */
export function initCardAnimations() {
  initStoryPillars();
  initGalleryCoverflow();
}

/**
 * Story Pillars Entrance Animation
 */
export function initStoryPillars() {
  if (storyPillarsInitialized) return;
  const pillars = document.querySelectorAll(".story-pillar-item");
  if (!pillars.length) return;

  storyPillarsInitialized = true;

  // Entrance scroll reveal
  gsap.from(pillars, {
    scrollTrigger: {
      trigger: ".story-pillars",
      start: "top 85%",
      once: true
    },
    opacity: 0,
    x: -30,
    duration: 0.8,
    stagger: 0.15,
    ease: "power2.out",
    clearProps: "transform,opacity"
  });

  // Tap / Click to toggle accordion
  pillars.forEach(item => {
    item.addEventListener("click", () => {
      const isAlreadyExpanded = item.classList.contains("is-expanded");

      // Accordion mode: collapse other pillars to keep screen clean & uncluttered
      pillars.forEach(p => {
        p.classList.remove("is-expanded");
        p.setAttribute("aria-expanded", "false");
      });

      // Toggle current pillar
      if (!isAlreadyExpanded) {
        item.classList.add("is-expanded");
        item.setAttribute("aria-expanded", "true");
      }
    });

    // Keyboard accessibility (Enter / Space)
    item.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        item.click();
      }
    });
  });
}

/**
 * 3D Coverflow Effect for Gallery Cards
 */
export function initGalleryCoverflow() {
  if (coverflowInitialized) return;
  const galleryItems = document.querySelectorAll(".gallery-item");
  if (!galleryItems.length) return;

  // On mobile & tablets (< 1024px), keep cards flat and stable (no 3D twisting on scroll)
  if (window.innerWidth < 1024) {
    galleryItems.forEach(item => {
      gsap.set(item, { rotateY: 0, z: 0, scale: 1, clearProps: "transform" });
    });
    return;
  }

  coverflowInitialized = true;

  galleryItems.forEach((item, i) => {
    ScrollTrigger.create({
      trigger: item,
      start: "top 85%",
      onEnter: () => updateCoverflow(i)
    });

    item.addEventListener("mouseenter", () => {
      gsap.to(item, {
        scale: 1.03,
        y: -6,
        duration: 0.35,
        ease: "power2.out"
      });
    });

    item.addEventListener("mouseleave", () => {
      gsap.to(item, {
        scale: 1,
        y: 0,
        duration: 0.35,
        ease: "power2.out"
      });
    });
  });

  window.addEventListener("resize", () => {
    if (window.innerWidth < 1024) {
      galleryItems.forEach(item => {
        gsap.set(item, { rotateY: 0, z: 0, scale: 1, clearProps: "transform" });
      });
    }
  });
}

export function updateCoverflow(activeIndex) {
  if (window.innerWidth < 1024) return; // Prevent tilt on mobile devices
  const items = document.querySelectorAll(".gallery-item");
  items.forEach((item, i) => {
    const offset = i - activeIndex;
    gsap.to(item, {
      rotateY: offset * -15,
      z: offset === 0 ? 30 : -Math.abs(offset) * 20,
      scale: offset === 0 ? 1.03 : 0.95,
      duration: 0.6,
      ease: "power3.out"
    });
  });
}
